import XLSX from 'sheetjs-style';
import { saveAs } from 'file-saver';
import { prettifyExcelDates } from '../../utils/prettifiers';
import { getLeadSourceName } from '../../utils/leadSource';
import { buildCrmFileFields, formatCrmNotesForFile } from './crmFileSchema';
import { buildSchemaFromLanding } from './utils';

const identity = value => value;

export const getCrmExportFieldId = field => field.isCustom ? `custom:${field.key}` : field.key;

const readCustomFields = lead => {
  const value = lead?.CustomField;
  if (typeof value === 'string') return [{ key: 'CustomField', value }];
  if (Array.isArray(value)) return value.filter(field => field?.key);
  return value?.key ? [value] : [];
};

export const buildCrmExportFields = ({ formSchema = [], leads = [], t = identity } = {}) => {
  // Include fields present in older leads even if the current form no longer contains them.
  const inferred = buildSchemaFromLanding({
    defaultFormFields: ['FullName', 'Name', 'Surname', 'Email', 'Tel', 'City']
      .filter(key => leads.some(lead => lead?.[key] != null)),
  });
  leads.forEach(lead => readCustomFields(lead).forEach(field => {
    inferred.push({
      key: field.key, label: field.key === 'CustomField' ? t('Custom Field') : field.key, isCustom: true,
    });
  }));

  const fields = [
    { key: 'Source', label: 'Source', isCustom: false },
    { key: 'Date', label: 'Date', isCustom: false },
    ...buildCrmFileFields(Array.isArray(formSchema) ? formSchema : []),
    ...inferred,
  ];
  const seen = new Set();
  const labels = new Set();
  return fields.filter(field => {
    const id = getCrmExportFieldId(field);
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  }).map(field => {
    let label = field.isCustom ? field.label : t(field.label);
    // A custom field named Notes must not overwrite the customer's actual notes.
    if (field.isCustom && labels.has(label)) label = `${label} (${t('Custom Field')})`;
    labels.add(label);
    return { ...field, label };
  });
};

export const buildCrmExportRow = (lead, fields, { columnTitle = '', t = identity } = {}) => {
  const row = {};
  fields.forEach(field => {
    let value;
    if (field.isCustom) {
      value = readCustomFields(lead).find(custom => custom.key === field.key)?.value ?? '';
    } else {
      switch (field.key) {
        case 'Source': value = t(getLeadSourceName(lead)); break;
        case 'Date': value = lead.createdAt ? prettifyExcelDates(lead.createdAt) || '' : ''; break;
        case 'Column': value = columnTitle; break;
        case 'Notes': value = formatCrmNotesForFile(lead.Notes, prettifyExcelDates); break;
        default: value = lead[field.key] ?? '';
      }
    }
    row[field.label] = value;
  });
  return row;
};

export const saveCrmExport = (rows, { fileName = 'crm-export', fileType = 'xlsx' } = {}) => {
  const bookType = ['xlsx', 'xls', 'csv'].includes(fileType) ? fileType : 'xlsx';
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(rows), 'Leads');
  const buffer = XLSX.write(workbook, { bookType, type: 'array' });
  const mimeTypes = {
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
    xls: 'application/vnd.ms-excel;charset=UTF-8',
    csv: 'text/csv;charset=utf-8',
  };
  const baseName = fileName.replace(/\.(xlsx|xls|csv)$/i, '');
  saveAs(new Blob([buffer], { type: mimeTypes[bookType] }), `${baseName}.${bookType}`);
};

export const exportCrmLeads = (leads, { columnTitles = {}, ...options } = {}) => {
  const fields = buildCrmExportFields({ ...options, leads });
  const rows = leads.map(lead => buildCrmExportRow(lead, fields, {
    t: options.t, columnTitle: columnTitles[lead._id || lead.id] || '',
  }));
  saveCrmExport(rows, options);
};
